import React, {useState} from 'react';

import cs from 'classnames';

import {AutoSizedTextArea} from 'app/components/general/AutoSizedTextArea';
import {useCurrentUserState} from 'app/current-user/api';
import {
  createFormErrorsContext,
  FormControlGroup,
  FormErrorsProps,
} from 'app/general/forms';
import {CheckboxButton} from 'app/general/widgets/CheckboxButton';
import {GeneralCreateItem} from 'app/general/widgets/GeneralCreateItem';
import {useAccountSettingsState} from 'app/settings/api';
import {CollapsableTeamPickerForm} from 'app/teams/components/CollapsableTeamPickerForm';
import {useCreateUser, useInviteUser} from 'app/user/api';
import {refreshPopoversPositions} from 'app/utils/browser';
import {gt} from 'app/utils/i18n';
import EmailPreview from 'app/welcome/components/EmailPreview';

import {GuestUserToggleField} from './GuestUserToggleField';

import type {NewInvitationType} from 'app/user/types';
import type {TeamSerializedType, UserSerializedType} from 'sweet-types';

export interface Props extends FormErrorsProps {
  showTeams: boolean;
  onCreateUser: (user: UserSerializedType) => Promise<unknown>;
  onConfirm?: () => Promise<unknown>;
  onCancel?: () => void;
  isPopover: boolean;
  className?: string;
  showCancelButton?: boolean;
  hideGuestInvite?: boolean;
  name?: string;
}

export const CreateUserForm = createFormErrorsContext<Props>(
  function CreateUserForm({
    showTeams,
    isPopover,
    captureFormErrorsFromRejection,
    onConfirm,
    onCreateUser,
    hideGuestInvite,
    name,
    ...other
  }: Props) {
    const {is_saml} = useAccountSettingsState().data!;
    const {is_super_manager} = useCurrentUserState().data!;

    const [createUser] = useCreateUser();
    const [inviteUser] = useInviteUser();

    const [isCreating, setIsCreating] = useState(false);
    const [allowEdit, setAllowEdits] = useState(false);
    const [selectedTeams, selectTeams] = useState<Array<TeamSerializedType>>(
      []
    );
    const [is_guest_until, set_is_guest_until] = useState<string | undefined>(
      undefined
    );

    const isGuest = is_guest_until != null;

    const [inputs, updateInputs] = useState({
      email: '',
      restrictSso: is_saml,
    });

    const [templateName, setName] = useState<string | undefined>(name);
    const [subject, setSubject] = useState<string | undefined>();
    const [welcome_message, setMessage] = useState<string>(
      gt(
        'You are invited to join SweetProcess, a new platform where you will be able to read our policy and procedure documentation.'
      )
    );

    const onError = (err: Error) => {
      captureFormErrorsFromRejection(err);
      setIsCreating(false);
      return err;
    };

    const inviteToTeams = async (
      user: UserSerializedType
    ): Promise<UserSerializedType> => {
      if (selectedTeams.length === 0) {
        return Promise.resolve(user);
      }

      const invitations = selectedTeams.map<NewInvitationType>((team) => ({
        send_mail: false,
        content_type: 'team',
        permission: allowEdit ? 'edit' : 'view',
        object_id: team.id,
        to_user_id: user.url,
      }));

      await inviteUser({invitations});

      return user;
    };

    const localCreateUser = (name: string): Promise<unknown> => {
      setIsCreating(true);

      const createUserEventChain = isGuest
        ? createUser({
            name,
            email: inputs.email,
            is_guest_until,
            welcome_subject: subject,
            welcome_message,
          }).unwrap()
        : createUser({
            name,
            email: inputs.email,
            restrict_saml_only_login: inputs.restrictSso,
            welcome_subject: subject,
            welcome_message,
          })
            .unwrap()
            .then(inviteToTeams);

      return createUserEventChain
        .then((user) => {
          if (onConfirm != null) {
            onConfirm();
          }
          return onCreateUser(user);
        })
        .catch(onError);
    };

    const handleInputChange = (
      event: React.SyntheticEvent<HTMLInputElement>
    ) => {
      const target = event.currentTarget;
      const value = target.type === 'checkbox' ? target.checked : target.value;
      const name = target.name;

      updateInputs({
        ...inputs,
        [name]: value,
      });
    };

    if (isPopover) {
      refreshPopoversPositions();
    }

    return (
      <div className="d-flex flex-column flex-md-row">
        <div
          className={cs('flex-grow-1', {
            'd-flex flex-column justify-content-between': isPopover,
          })}
        >
          <GeneralCreateItem
            {...other}
            confirmDisabled={
              showTeams && !is_super_manager && selectedTeams.length === 0
            }
            defaultName={name}
            isPopover={isPopover}
            isCreating={isCreating}
            namePlaceholder={
              !isGuest ? gt("Member's name") : gt("Guest's name")
            }
            createLabel={!isGuest ? gt('Add member') : gt('Add Guest')}
            onCreate={localCreateUser}
            onChangeName={setName}
          >
            <FormControlGroup field="email">
              <input
                className={cs('form-control', {'form-control-sm': isPopover})}
                type="email"
                maxLength={64}
                name="email"
                placeholder={
                  !isGuest ? gt("Member's email") : gt("Guest's email")
                }
                onChange={handleInputChange}
                value={inputs.email}
              />
            </FormControlGroup>

            {!hideGuestInvite ? (
              <GuestUserToggleField
                is_guest_until={is_guest_until}
                onChange={set_is_guest_until}
              />
            ) : null}

            {is_saml && !isGuest && (
              <FormControlGroup field="restrictSso">
                <div className="form-check">
                  <input
                    className="form-check-input"
                    type="checkbox"
                    name="restrictSso"
                    onChange={handleInputChange}
                    checked={inputs.restrictSso}
                    id="restrictSsoId"
                  />
                  <label
                    className="form-check-label"
                    htmlFor="restrictSsoId"
                    title={gt('New member will only be able to login via SSO')}
                  >
                    {gt('Restrict SSO login')}
                  </label>
                </div>
              </FormControlGroup>
            )}
            {showTeams && !isGuest ? (
              <div className="my-2">
                <CollapsableTeamPickerForm
                  disabledText={(team) =>
                    team.can_edit
                      ? undefined
                      : gt('Only managers can invite to team')
                  }
                  selected={selectedTeams}
                  onChange={selectTeams}
                >
                  {selectedTeams.length > 0 ? (
                    <div className="small">
                      <div className="form-check my-2">
                        <CheckboxButton
                          id="give-manager-access-checkbox"
                          className="form-check-input"
                          onClick={() => setAllowEdits(!allowEdit)}
                          isChecked={allowEdit}
                          useRealCheckbox={true}
                        />
                        <label
                          className="form-check-label"
                          htmlFor="give-manager-access-checkbox"
                        >
                          {gt('Give manager access to selected teams')}
                        </label>
                      </div>
                    </div>
                  ) : null}
                </CollapsableTeamPickerForm>
              </div>
            ) : null}
            <div>
              <FormControlGroup
                key="welcome_subject"
                className="mb-0 mt-2"
                field="welcome_subject"
              >
                <label htmlFor="subject" className="form-label small">
                  {gt('Welcome Email Subject')}
                </label>
                <div className="input-group">
                  <input
                    type="text"
                    className={cs('form-control', {
                      'form-control-sm': isPopover,
                    })}
                    autoFocus={false}
                    name={'subject'}
                    onChange={(event) => setSubject(event.target.value)}
                    value={subject ?? ''}
                    placeholder={gt('Welcome Email Subject')}
                  />
                </div>
              </FormControlGroup>
              <FormControlGroup
                key="welcome_message"
                className="mb-3 mt-2"
                field="welcome_message"
              >
                <label htmlFor="message" className="form-label small">
                  {gt('Welcome Email Message')}
                </label>
                <div className="input-group">
                  <AutoSizedTextArea
                    id="message"
                    placeholder={gt('Welcome Email Message')}
                    className={cs('form-control', {
                      'form-control-sm': isPopover,
                    })}
                    value={welcome_message}
                    onChange={setMessage}
                    onBlur={() => setMessage(welcome_message)}
                  />
                </div>
              </FormControlGroup>
            </div>
          </GeneralCreateItem>
        </div>
        <div className="flex-shrink-0 ms-3 border-left ml-3 mt-sm-3 mt-md-3 mt-lg-0 mt-xl-0 d-none d-lg-block">
          <EmailPreview
            templateEmail={inputs.email}
            templateName={templateName}
            subject={subject}
            welcome_message={welcome_message}
            setSubject={setSubject}
          />
        </div>
      </div>
    );
  }
);
